import { useCallback, useEffect, useState } from "react";
import {
  Download,
  History,
  Paperclip,
  Plus,
  Trash2,
  WalletCards,
  X,
} from "lucide-react";
import {
  api,
  type CertificationEntityResources,
  type CertificationEntityType,
} from "./api";
import {
  ATTACHMENT_ACCEPT,
  attachmentSelectionError,
} from "./attachment-policy";
import { DatePicker } from "./date-picker";
import {TimelineDialog} from "./timeline-dialog";
import {RecordTrashAction} from "./record-trash-action";

const money = (value = 0) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(
    Number(value) || 0,
  );
const date = (value?: string) =>
  value
    ? new Date(
        value.includes("T") ? value : `${value.slice(0, 10)}T12:00:00`,
      ).toLocaleDateString("pt-BR")
    : "Não informado";
const bytes = (value = 0) =>
  value >= 1_000_000
    ? `${(value / 1_000_000).toFixed(1)} MB`
    : `${Math.ceil(value / 1_000)} KB`;
const value = (form: FormData, name: string) => String(form.get(name) || "");
const numberValue = (form: FormData, name: string) =>
  Number(String(form.get(name) || "0").replace(",", ".")) || 0;
const fileAsBase64 = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(",")[1] || "");
    reader.onerror = () => reject(new Error("Não foi possível ler o arquivo."));
    reader.readAsDataURL(file);
  });
const timelineActionLabels:Record<string,string>={kanban_column_change:"Status do Kanban alterado",create:"Registro criado",update:"Registro atualizado",add_cost:"Gasto adicionado",add_attachment:"Anexo adicionado",delete_cost:"Gasto removido",delete_attachment:"Anexo removido"};
function timelineContext(value?:string){try{return value?JSON.parse(value):{}}catch{return{}}}

export function CertificationEntityResourcesPanel({
  entityType,
  entityId,
  recordName,
  onDeleted,
  onError,
  showCosts = true,
}: {
  entityType: CertificationEntityType;
  entityId: string;
  recordName: string;
  onDeleted: () => void;
  onError: (message: string) => void;
  showCosts?: boolean;
}) {
  const [data, setData] = useState<CertificationEntityResources>();
  const [dialog, setDialog] = useState<"cost" | "attachment" | "timeline" | null>(
    null,
  );
  const [saving, setSaving] = useState(false);
  const load = useCallback(
    () =>
      api
        .certificationEntityResources(entityType, entityId)
        .then(setData)
        .catch((error) => onError(error.message)),
    [entityType, entityId, onError],
  );
  useEffect(() => {
    load();
  }, [load]);
  const submitCost = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setSaving(true);
    try {
      await api.addCertificationEntityCost(entityType, entityId, {
        type: value(form, "type"),
        description: value(form, "description"),
        supplier: value(form, "supplier"),
        costDate: value(form, "costDate"),
        plannedValue: numberValue(form, "plannedValue"),
        actualValue: numberValue(form, "actualValue"),
        notes: value(form, "notes"),
      });
      setDialog(null);
      await load();
    } catch (error) {
      onError((error as Error).message);
    } finally {
      setSaving(false);
    }
  };
  const submitAttachment = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget),
      file = form.get("file") as File;
    const error = attachmentSelectionError(
      file?.size ? [file] : [],
      data?.attachmentBytes || 0,
    );
    if (error) return onError(error);
    setSaving(true);
    try {
      await api.addCertificationEntityAttachment(entityType, entityId, {
        fileName: file.name,
        mimeType: file.type || "application/octet-stream",
        size: file.size,
        contentBase64: await fileAsBase64(file),
        description: value(form, "description"),
      });
      setDialog(null);
      await load();
    } catch (reason) {
      onError((reason as Error).message);
    } finally {
      setSaving(false);
    }
  };
  const openAttachment = async (id: string) => {
    try {
      const blob = await api.certificationEntityAttachmentContent(
          entityType,
          entityId,
          id,
        ),
        url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (error) {
      onError((error as Error).message);
    }
  };
  const removeCost = async (id: string) => {
    try {
      await api.deleteCertificationEntityCost(entityType, entityId, id);
      await load();
    } catch (error) {
      onError((error as Error).message);
    }
  };
  const removeAttachment = async (id: string) => {
    try {
      await api.deleteCertificationEntityAttachment(entityType, entityId, id);
      await load();
    } catch (error) {
      onError((error as Error).message);
    }
  };
  return (
    <section className="card workflow-section certification-resources">
      <div className="section-heading">
        <div>
          <span className="eyebrow">Financeiro e arquivos</span>
          <h3>{showCosts ? "Gastos e anexos" : "Anexos do registro"}</h3>
          <p>Registre valores e documentos relacionados a este item.</p>
        </div>
        <div className="resource-actions">
          {showCosts && (
            <button className="button" onClick={() => setDialog("cost")}>
              <Plus />
              Adicionar gasto
            </button>
          )}
          <button className="button" onClick={() => setDialog("attachment")}>
            <Paperclip />
            Adicionar anexo
          </button>
          <button className="button" disabled={!data} onClick={() => setDialog("timeline")}>
            <History />
            Linha do tempo
          </button>
          <RecordTrashAction label={recordName} onDelete={async()=>{try{await api.deleteCertificationEntity(entityType,entityId);onDeleted()}catch(error){onError((error as Error).message);throw error}}}/>
        </div>
      </div>
      {!data ? (
        <div className="muted">Carregando gastos e anexos…</div>
      ) : (
        <div className="resource-columns">
          {showCosts && (
            <div className="resource-group">
              <header>
                <div>
                  <WalletCards />
                  <b>Gastos</b>
                </div>
                <strong>
                  {money(
                    data.costs.reduce(
                      (sum, item) => sum + Number(item.actual_value || 0),
                      0,
                    ),
                  )}
                </strong>
              </header>
              {data.costs.length ? (
                <div className="resource-list">
                  {data.costs.map((item) => (
                    <article key={item.id}>
                      <div>
                        <b>{item.description}</b>
                        <small>
                          {item.type} · {date(item.cost_date)}
                          {item.supplier ? ` · ${item.supplier}` : ""}
                        </small>
                      </div>
                      <span>{money(item.actual_value)}</span>
                      <button
                        className="icon-button danger"
                        aria-label="Excluir gasto"
                        title="Excluir gasto"
                        onClick={() => removeCost(item.id)}
                      >
                        <Trash2 />
                      </button>
                    </article>
                  ))}
                </div>
              ) : (
                <p className="muted">Nenhum gasto registrado.</p>
              )}
            </div>
          )}
          <div className="resource-group">
            <header>
              <div>
                <Paperclip />
                <b>Anexos</b>
              </div>
              <strong>{bytes(data.attachmentBytes)} / 30 MB</strong>
            </header>
            {data.attachments.length ? (
              <div className="resource-list">
                {data.attachments.map((item) => (
                  <article key={item.id}>
                    <div>
                      <b>{item.file_name}</b>
                      <small>
                        {bytes(item.size)} · {item.uploaded_by_name} ·{" "}
                        {date(item.uploaded_at)}
                      </small>
                    </div>
                    <button
                      className="icon-button"
                      aria-label="Abrir anexo"
                      title="Abrir anexo"
                      onClick={() => openAttachment(item.id)}
                    >
                      <Download />
                    </button>
                    <button
                      className="icon-button danger"
                      aria-label="Excluir anexo"
                      title="Excluir anexo"
                      onClick={() => removeAttachment(item.id)}
                    >
                      <Trash2 />
                    </button>
                  </article>
                ))}
              </div>
            ) : (
              <p className="muted">Nenhum anexo registrado.</p>
            )}
          </div>
        </div>
      )}
      {dialog==="timeline"&&data&&<TimelineDialog title={recordName} description="Histórico de alterações e movimentações deste item." events={data.timeline.slice().reverse().map(item=>{const context=timelineContext(item.context);return{id:item.id,title:context.columnName||timelineActionLabels[item.action]||"Atualização registrada",detail:context.comment||(context.columnName?`Movido para ${context.columnName}`:"Alteração registrada."),author:item.user_name,at:item.created_at,color:context.columnColor||context.color||"#4f46e5"}})} emptyMessage="Nenhuma movimentação registrada neste item." onClose={()=>setDialog(null)}/>}
      {dialog === "cost" && (
        <ResourceModal title="Adicionar gasto" onClose={() => setDialog(null)}>
          <form className="modal-form compact-modal-form" onSubmit={submitCost}>
            <div className="grid">
              <label>
                Tipo
                <input
                  name="type"
                  required
                  placeholder="TAXA, AUDITORIA, ENSAIO…"
                />
              </label>
              <label>
                Data
                <DatePicker
                  name="costDate"
                  required
                  defaultValue={new Date().toISOString().slice(0, 10)}
                  aria-label="Data do gasto"
                />
              </label>
              <label>
                Valor planejado
                <input
                  name="plannedValue"
                  inputMode="decimal"
                  defaultValue="0"
                />
              </label>
              <label>
                Valor realizado
                <input
                  name="actualValue"
                  inputMode="decimal"
                  defaultValue="0"
                />
              </label>
              <label className="span-2">
                Descrição
                <input name="description" required />
              </label>
              <label className="span-2">
                Fornecedor
                <input name="supplier" />
              </label>
              <label className="wide">
                Observações
                <textarea name="notes" rows={2} />
              </label>
            </div>
            <div className="actions">
              <button
                className="button"
                type="button"
                onClick={() => setDialog(null)}
              >
                Cancelar
              </button>
              <button className="button primary" disabled={saving}>
                {saving ? "Salvando…" : "Adicionar gasto"}
              </button>
            </div>
          </form>
        </ResourceModal>
      )}
      {dialog === "attachment" && (
        <ResourceModal title="Adicionar anexo" onClose={() => setDialog(null)}>
          <form className="modal-form" onSubmit={submitAttachment}>
            <div className="grid">
              <label className="wide">
                Arquivo
                <input
                  type="file"
                  name="file"
                  accept={ATTACHMENT_ACCEPT}
                  required
                />
              </label>
              <label className="wide">
                Descrição
                <input name="description" />
              </label>
            </div>
            <p className="attachment-hint">
              Formatos permitidos: ZIP, RAR, imagens, planilhas, vídeos, áudios
              e PDF. Limite total de 30 MB neste registro.
            </p>
            <div className="actions">
              <button
                className="button"
                type="button"
                onClick={() => setDialog(null)}
              >
                Cancelar
              </button>
              <button className="button primary" disabled={saving}>
                {saving ? "Enviando…" : "Adicionar anexo"}
              </button>
            </div>
          </form>
        </ResourceModal>
      )}
    </section>
  );
}

function ResourceModal({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <div
      className="dialog-backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section className="module-dialog" role="dialog" aria-modal="true">
        <header>
          <h2>{title}</h2>
          <button className="icon-button" onClick={onClose} aria-label="Fechar">
            <X />
          </button>
        </header>
        {children}
      </section>
    </div>
  );
}
